import {createSelector, createSlice} from '@reduxjs/toolkit';
import {
	EntityType, Entry,
	PropertyDefinition,
	Repository,
	RepositoryEntry,
} from '../types';
import {
	selectAllEntries,
	selectLocationId,
} from './contentSlice';
import {PROPERTY_DEFINITION_TYPES} from '../constants';
import {populateEntryData, sortEntryByRank} from '../common/util';

const repositoryViewSlice = createSlice({
	name: 'repositoryView',
	initialState: {},
	reducers: {},
});

export const selectRepository = createSelector(
	[selectAllEntries, selectLocationId],
	(allEntries, locationId) => {
		const repository = allEntries.find((entry) => entry.type === EntityType.Repository && entry.id === locationId);
		return repository ? repository as Repository : null;
	},
);

export const selectRepositoryEntry = createSelector(
	[selectAllEntries, selectLocationId],
	(allEntries, locationId) => {
		const repositoryEntry = allEntries.find((entry) => entry.type === EntityType.RepositoryEntry && entry.id === locationId);
		return repositoryEntry ? repositoryEntry as RepositoryEntry : null;
	},
);

export const selectRepositoryPropertyDefinitions = createSelector([selectRepository, selectAllEntries], (repository, allEntries) => {
	if (!repository) {
		return [];
	}
	const [firstPropertyDef, ...restDefs] = allEntries
		.filter((entry) => PROPERTY_DEFINITION_TYPES.includes(entry.type) && entry.parentId === repository.id)
		.sort(sortEntryByRank);
	return (firstPropertyDef ? [{...firstPropertyDef, autoFocus: true}, ...restDefs] : []) as PropertyDefinition[];
});

export const selectIsEntriesTabEnabled = createSelector([selectRepositoryPropertyDefinitions], (repositoryPropertyDefinitions) => Boolean(repositoryPropertyDefinitions.length));

export const selectRepositoryPropertyDefinitionsPopulated = createSelector([selectRepositoryPropertyDefinitions], (propertyDefinitions) => {
	return propertyDefinitions.map((def) => populateEntryData(def as Entry));
});

export const selectRepositoryEntries = createSelector([selectRepository, selectAllEntries], (repository, allEntries) => {
	if (!repository) {
		return [];
	}
	return allEntries.filter((entry) => entry.type === EntityType.RepositoryEntry && entry.repositoryId === repository.id) as RepositoryEntry[];
});

export default repositoryViewSlice.reducer;
